import * as React from 'react';
import styled from 'styled-components';
import ValidateIcon from 'assets/icons/indicators/ValidateIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import { BodyRegular, FlexEnd, FlexMd } from 'components/Elements';
import { theme, sizes } from 'theme';

const TAB_PEEK_PX = 50; // ensure consistent tab preview

export type LearnerTabDefinition = {
    id: string;
    name: string;
    icon: React.ElementType;
    content: JSX.Element;
    action: () => void;
    complete?: boolean;
    hideCompleteIcon?: boolean;
};

export default function LearnerTabs({
    tabs,
    activeTabId,
    onClickTab,
}: {
    tabs: LearnerTabDefinition[];
    activeTabId: string;
    onClickTab: (newId: string) => void;
}): JSX.Element {
    const scrollerRef = React.useRef<HTMLDivElement>(null);
    const tabRefs = React.useRef(new Map<string, HTMLButtonElement | null>());

    const activeIdRef = React.useRef(activeTabId);
    React.useEffect(() => {
        activeIdRef.current = activeTabId;
    }, [activeTabId]);

    const revealActive = React.useCallback(() => {
        const scroller = scrollerRef.current;
        const node = tabRefs.current.get(activeIdRef.current);
        if (!scroller || !node) return;

        const viewLeft = scroller.scrollLeft;
        const viewRight = viewLeft + scroller.clientWidth;

        // visible area with peek padding on both sides
        const paddedLeft = viewLeft + TAB_PEEK_PX;
        const paddedRight = viewRight - TAB_PEEK_PX;

        const nodeLeft = node.offsetLeft;
        const nodeRight = nodeLeft + node.offsetWidth;

        let nextLeft = viewLeft;

        // if node is left of the padded area scroll left so node starts at peek
        if (nodeLeft < paddedLeft) {
            nextLeft = nodeLeft - TAB_PEEK_PX;
        }
        // if node is right of the padded area scroll right so node ends at peek
        else if (nodeRight > paddedRight) {
            nextLeft = nodeRight + TAB_PEEK_PX - scroller.clientWidth;
        } else {
            // already fully in the padded viewport no move
            return;
        }

        const maxLeft = scroller.scrollWidth - scroller.clientWidth;
        if (nextLeft < 0) nextLeft = 0;
        if (nextLeft > maxLeft) nextLeft = maxLeft;

        if (Math.abs(nextLeft - viewLeft) > 1) {
            scroller.scrollTo({ left: nextLeft, behavior: 'smooth' });
        }
    }, []);

    React.useEffect(() => {
        revealActive();
    }, [revealActive, tabs.length, activeTabId]);

    return (
        <TabsContainer>
            <Viewport>
                <Scroller ref={scrollerRef} role="tablist" aria-label="Module navigation">
                    <TabsRow>
                        {tabs.map((tabDefinition) => {
                            const Icon = tabDefinition.icon;
                            const isActive = tabDefinition.id === activeTabId;

                            function renderIcon() {
                                if (tabDefinition.hideCompleteIcon) return null;
                                return tabDefinition.complete ? (
                                    <ValidateIcon />
                                ) : (
                                    <ValidateIcon colour={theme.textColour} inverse />
                                );
                            }

                            return isActive ? (
                                <ActiveTab
                                    key={tabDefinition.id}
                                    ref={(element: HTMLButtonElement | null) =>
                                        tabRefs.current.set(tabDefinition.id, element)
                                    }
                                    role={'tab'}
                                    aria-label={tabDefinition.name}
                                    aria-selected={isActive}
                                    type={'button' as const}
                                >
                                    <TextSection>
                                        <Icon colour={theme.textColour} size={IconSize.MEDIUM} />
                                        <BodyRegular $nowrap $bold>
                                            {tabDefinition.name}
                                        </BodyRegular>
                                        {renderIcon()}
                                    </TextSection>
                                </ActiveTab>
                            ) : (
                                <Tab
                                    key={tabDefinition.id}
                                    ref={(element: HTMLButtonElement | null) =>
                                        tabRefs.current.set(tabDefinition.id, element)
                                    }
                                    role={'tab'}
                                    aria-label={tabDefinition.name}
                                    aria-selected={isActive}
                                    type={'button' as const}
                                    onClick={() => {
                                        onClickTab(tabDefinition.id);
                                        tabDefinition.action();
                                    }}
                                >
                                    <TextSection>
                                        <Icon colour={theme.textColour} size={IconSize.MEDIUM} />
                                        <BodyRegular $nowrap $bold>
                                            {tabDefinition.name}
                                        </BodyRegular>
                                        {renderIcon()}
                                    </TextSection>
                                </Tab>
                            );
                        })}
                    </TabsRow>
                </Scroller>
            </Viewport>
        </TabsContainer>
    );
}

const TabsContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const Viewport = styled.div`
    width: 100%;
    overflow: hidden;
`;

const Scroller = styled.div`
    display: block;
    padding-inline: ${TAB_PEEK_PX}px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    touch-action: pan-x;
    scrollbar-width: none;
`;

const TabsRow = styled(FlexEnd)`
    height: 80px;
    height: ${sizes.learnerTabsHeight};
    width: max-content;
    gap: 12px;
    margin-inline: auto;
    white-space: nowrap;
`;

const TextSection = styled(FlexMd)`
    align-items: center;
`;

const Tab = styled.button`
    display: flex;
    flex-direction: column;
    flex: 0 0 200px;
    justify-content: center;
    padding: ${sizes.spacingMd} ${sizes.spacingRg};
    border: none;
    border-radius: ${sizes.borderRadiusLg} ${sizes.borderRadiusLg} 0 0;
    height: 100%;
    cursor: pointer;
    background: ${theme.learnerTabBackground};
    box-shadow: 4px 4px 10px 0px rgba(0, 0, 0, 0.25);
    scroll-margin-inline: ${TAB_PEEK_PX}px;

    &:focus {
        border: 2px solid ${theme.navigationBar.buttonFocusBorder};
        outline: ${theme.navigationBar.buttonFocusBorder};
        background: ${theme.navigationBar.buttonFocusBackground};
    }
`;

const ActiveTab = styled(Tab)`
    border-color: ${theme.cardBorder} ${theme.cardBorder} ${theme.appBackgroundColour};
    background: ${theme.appBackgroundColour};
`;
